/**
 * Guest ("saved on this device") job saves. Guests have no account, so a save
 * lives in `localStorage` only — never a cookie (cacheable pages must not emit
 * `Set-Cookie`, and the server has no use for the list). Every storage access
 * is wrapped: private mode / blocked site data must degrade to "not saved",
 * never throw into a click handler.
 */

export const GUEST_SAVED_JOBS_KEY = "ld_guest_saved_jobs";
export const GUEST_SAVED_JOBS_CAP = 50;
/** Guest save count at which the keep-your-saves signup sheet opens. */
export const GUEST_SAVE_PROMPT_AT = 3;
/** Window event the save button dispatches to open `GuestSaveSheet`. */
export const GUEST_SAVE_PROMPT_EVENT = "ld:guest-save-prompt";

export function readGuestSavedJobIds(): string[] {
	try {
		const raw = window.localStorage.getItem(GUEST_SAVED_JOBS_KEY);
		if (!raw) return [];
		const parsed: unknown = JSON.parse(raw);
		if (!Array.isArray(parsed)) return [];
		return parsed.filter((id): id is string => typeof id === "string");
	} catch {
		return [];
	}
}

function writeGuestSavedJobIds(ids: string[]): boolean {
	try {
		window.localStorage.setItem(GUEST_SAVED_JOBS_KEY, JSON.stringify(ids));
		return true;
	} catch {
		return false;
	}
}

export function isGuestJobSaved(jobId: string): boolean {
	return readGuestSavedJobIds().includes(jobId);
}

export type GuestToggleResult = {
	/** State the UI should show after the toggle. */
	isSaved: boolean;
	/** Number of device-saved jobs after the toggle. */
	count: number;
	/** False when storage refused the write (private mode, quota). */
	persisted: boolean;
};

/**
 * Toggle `jobId` in the device list. Newest first; the oldest id falls off
 * once the list passes {@link GUEST_SAVED_JOBS_CAP}.
 */
export function toggleGuestSavedJob(jobId: string): GuestToggleResult {
	const current = readGuestSavedJobIds();
	const wasSaved = current.includes(jobId);
	const next = wasSaved
		? current.filter((id) => id !== jobId)
		: [jobId, ...current].slice(0, GUEST_SAVED_JOBS_CAP);
	const persisted = writeGuestSavedJobIds(next);
	return { isSaved: !wasSaved, count: next.length, persisted };
}
